import { useCallback, useEffect, useState } from "react";
import { API_BASE, apiFetch } from "../api";

const request = async (path, method = "GET", body) => {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = res.status === 204 ? null : await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || "Something went wrong.");
  return data;
};

// Groups are lists of people you can share tasks with. Every action returns a
// promise that rejects with a readable message.
export const useGroups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(
    () =>
      request("/groups")
        .then(setGroups)
        .catch(() => {})
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const replace = (group) => setGroups((prev) => prev.map((g) => (g.id === group.id ? group : g)));

  // Everyone in any of my groups, once each: the people I can share a task with.
  const contacts = [];
  const seen = new Set();
  for (const g of groups) {
    for (const m of g.members) {
      if (!seen.has(m.userId)) {
        seen.add(m.userId);
        contacts.push(m);
      }
    }
  }

  return {
    groups,
    contacts,
    loading,
    reload,
    search: (q) => request(`/users/search?q=${encodeURIComponent(q)}`).then((d) => d.user),
    create: (name) => request("/groups", "POST", { name }).then((g) => setGroups((prev) => [...prev, g])),
    rename: (id, name) => request(`/groups/${id}`, "PATCH", { name }).then(replace),
    remove: (id) => request(`/groups/${id}`, "DELETE").then(() => setGroups((prev) => prev.filter((g) => g.id !== id))),
    addMember: (id, identifier) => request(`/groups/${id}/members`, "POST", { identifier }).then(replace),
    leave: (id) => request(`/groups/${id}/leave`, "DELETE").then(() => setGroups((prev) => prev.filter((g) => g.id !== id))),
    removeMember: (id, userId) => request(`/groups/${id}/members/${userId}`, "DELETE").then(replace),
  };
};
